import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { Image, Film, Music, FileText, User, FolderOpen, Download, Cloud, AlertTriangle } from 'lucide-react';
import { API, PreviewContent, downloadFile } from './studio/shared';
import { ImageStudio } from './studio/ImageStudio';
import { VideoStudio } from './studio/VideoStudio';
import { MusicStudio } from './studio/MusicStudio';
import { AvatarStudio } from './studio/AvatarStudio';
import { TextStudio } from './studio/TextStudio';
import { LibraryView } from './studio/LibraryView';

export default function ContentStudioPage() {
  const [searchParams] = useSearchParams();
  // v3.3 (2026-07-01): honor ?tab=<key> so cross-page hand-offs (e.g. Influencer
  // Studio → Avatar Studio) can deep-link straight to the right sub-tab.
  const initialTab = (() => {
    const t = searchParams.get('tab');
    return ['image', 'video', 'music', 'avatar', 'text', 'library'].includes(t) ? t : 'image';
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [library, setLibrary] = useState([]);
  const [stats, setStats] = useState({});
  const [driveStatus, setDriveStatus] = useState({ connected: false });
  const [loading, setLoading] = useState(true);
  const [libraryFilter, setLibraryFilter] = useState('all');
  const [previewItem, setPreviewItem] = useState(null);

  const fetchLibrary = useCallback(async () => {
    try {
      const [lib, st, ds] = await Promise.all([
        axios.get(`${API}/api/creative-studio/library`),
        axios.get(`${API}/api/creative-studio/stats`),
        axios.get(`${API}/api/drive/status`).catch(() => ({ data: { connected: false } })),
      ]);
      setLibrary(lib.data.items || []);
      setStats(st.data || {});
      setDriveStatus(ds.data);
    } catch { /* */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchLibrary(); }, [fetchLibrary]);

  const saveToLibrary = async (data) => {
    try {
      await axios.post(`${API}/api/creative-studio/library/save`, data);
      toast.success('Saved to library!');
      fetchLibrary();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const saveToDrive = async (itemId) => {
    try {
      await axios.post(`${API}/api/creative-studio/library/${itemId}/save-to-drive`, {});
      toast.success('Saved to Google Drive!');
      fetchLibrary();
    } catch (err) { toast.error(err.response?.data?.detail || 'Drive save failed'); }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Delete this item?')) return;
    try { await axios.delete(`${API}/api/creative-studio/library/${itemId}`); toast.success('Deleted'); fetchLibrary(); } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="creative-studio-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Creative Studio<PageInfoTooltip pageKey="content-studio" /></h1>
          <p className="text-gray-medium text-sm mt-1">Professional AI content generation — images, videos, music, avatars & text</p>
        </div>
        <div className="flex items-center gap-2">
          {stats.expiring_soon > 0 && <Badge className="bg-amber-100 text-amber-700 text-xs gap-1"><AlertTriangle className="h-3 w-3" />{stats.expiring_soon} expiring soon</Badge>}
          <Badge className="bg-gray-100 text-gray-600 text-xs">{stats.total || 0} in library</Badge>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border border-gray-border w-full sm:w-auto overflow-x-auto flex-nowrap justify-start">
          <TabsTrigger value="image" className="text-xs gap-1.5" data-testid="tab-image"><Image className="h-3.5 w-3.5" />Image Studio</TabsTrigger>
          <TabsTrigger value="video" className="text-xs gap-1.5" data-testid="tab-video"><Film className="h-3.5 w-3.5" />Video Studio</TabsTrigger>
          <TabsTrigger value="music" className="text-xs gap-1.5" data-testid="tab-music"><Music className="h-3.5 w-3.5" />Music Studio</TabsTrigger>
          <TabsTrigger value="avatar" className="text-xs gap-1.5" data-testid="tab-avatar"><User className="h-3.5 w-3.5" />Avatar Studio</TabsTrigger>
          <TabsTrigger value="text" className="text-xs gap-1.5" data-testid="tab-text"><FileText className="h-3.5 w-3.5" />Text Content</TabsTrigger>
          <TabsTrigger value="library" className="text-xs gap-1.5" data-testid="tab-library"><FolderOpen className="h-3.5 w-3.5" />Library</TabsTrigger>
        </TabsList>

        <TabsContent value="image"><ImageStudio onSave={saveToLibrary} /></TabsContent>
        <TabsContent value="video"><VideoStudio onSave={saveToLibrary} /></TabsContent>
        <TabsContent value="music"><MusicStudio onSave={saveToLibrary} /></TabsContent>
        <TabsContent value="avatar"><AvatarStudio /></TabsContent>
        <TabsContent value="text"><TextStudio onSave={saveToLibrary} /></TabsContent>
        <TabsContent value="library">
          <LibraryView items={library} filter={libraryFilter} setFilter={setLibraryFilter}
            driveConnected={driveStatus.connected} onSaveDrive={saveToDrive} onDelete={deleteItem}
            onPreview={setPreviewItem} loading={loading} />
        </TabsContent>
      </Tabs>

      {previewItem && (
        <Dialog open={!!previewItem} onOpenChange={() => setPreviewItem(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-sm truncate">{previewItem.title}</DialogTitle></DialogHeader>
            <PreviewContent item={previewItem} />
            <DialogFooter className="gap-2 flex-wrap">
              {previewItem.url && <Button size="sm" variant="outline" onClick={() => downloadFile(previewItem.url, `konnect_${previewItem.type}_${Date.now()}`)} data-testid="preview-download"><Download className="h-3.5 w-3.5 mr-1" />Download</Button>}
              {driveStatus.connected && !previewItem.saved_to_drive && (
                <Button size="sm" onClick={() => { saveToDrive(previewItem.item_id); setPreviewItem(null); }}><Cloud className="h-3.5 w-3.5 mr-1" />Save to Drive</Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
